import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { publicRequest, privateRequest } from "../utils/api";

// Types based on backend models and API responses
export interface ProjectTranslation {
  id?: number;
  project_id?: number;
  locale: string;
  title: string;
  description?: string;
  slug: string;
}

export interface MediaFile {
  id: number;
  name: string;
  path: string;
  mime_type: string;
  size: number;
  model_type: string;
  model_id: number;
}

export interface SeoMeta {
  id: number;
  title?: string;
  description?: string;
  keywords?: string;
  model_type: string;
  model_id: number;
}

export interface ProjectCoordinator {
  name: string;
  role: string;
  image?: string;
  bio?: string;
}

export interface Project {
  id: number;
  status: string;
  goal_amount: number;
  raised_amount: number;
  currency: string;
  featured: boolean;
  hero_image?: string;
  aim?: string;
  current_state?: string;
  start_date?: string;
  end_date?: string;
  location?: string;
  category?: string;
  coordinators?: ProjectCoordinator[];
  gallery?: string[];
  created_by?: number;
  updated_by?: number;
  deleted_by?: number;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  translations?: ProjectTranslation[];
  media?: MediaFile[];
  seoMeta?: SeoMeta;
}

export interface ProjectsResponse {
  projects: Project[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface ProjectsQueryParams {
  per_page?: number;
  page?: number;
  locale?: string;
}

// Query keys
export const PROJECTS_QUERY_KEY = ["projects"];
export const PROJECT_QUERY_KEY = (id: number) => ["projects", id];

// Fetch projects with pagination
const fetchProjects = async (
  params: ProjectsQueryParams = {}
): Promise<ProjectsResponse> => {
  try {
    const response = await publicRequest.get("/projects", { params });

    return response.data.data;
  } catch (error) {
    console.error("Error fetching projects:", error);
    throw error;
  }
};

// Fetch single project
const fetchProject = async (id: number): Promise<Project> => {
  const response = await publicRequest.get(`/projects/${id}`);
  return response.data.data;
};

// Create project (admin only)
const createProject = async (
  projectData: Omit<Project, "id" | "created_at" | "updated_at" | "deleted_at">
): Promise<Project> => {
  try {
    const response = await privateRequest.post("/projects", projectData);
    return response.data.data;
  } catch (error) {
    // Ensure error has a message property
    if (error && typeof error === "object" && !("message" in error)) {
      (error as Error).message = "An error occurred while creating the project";
    }
    throw error;
  }
};

// Update project (admin only)
const updateProject = async ({
  id,
  projectData,
}: {
  id: number;
  projectData: Partial<Project>;
}): Promise<Project> => {
  const response = await privateRequest.put(`/projects/${id}`, projectData);
  return response.data.data;
};

// Delete project (admin only)
const deleteProject = async (id: number): Promise<void> => {
  await privateRequest.delete(`/projects/${id}`);
};

// Custom hooks
export const useProjects = (params: ProjectsQueryParams = {}) => {
  return useQuery({
    queryKey: [...PROJECTS_QUERY_KEY, params],
    queryFn: () => fetchProjects(params),
    staleTime: 0, // Always refetch on mount
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useProject = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: PROJECT_QUERY_KEY(id),
    queryFn: () => fetchProject(id),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProject,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PROJECT_QUERY_KEY(data.id) });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
    },
  });
};

// Hook to fetch and map a single project for detail view
export const useProjectDetail = (id: number) => {
  const { data: apiProject, isLoading, error } = useProject(id);
  const mappedProject = apiProject
    ? {
        id: apiProject.id?.toString() || "", // Convert to string to match component expectations, handle undefined
        title:
          apiProject.translations?.find((t) => t.locale === "en")?.title ||
          apiProject.translations?.[0]?.title ||
          "",
        description:
          apiProject.translations?.find((t) => t.locale === "en")
            ?.description ||
          apiProject.translations?.[0]?.description ||
          "",
        aim: apiProject.aim || "",
        currentState: apiProject.current_state || "",
        image: apiProject.hero_image || "",
        gallery: apiProject.gallery || [],
        currentFunding: apiProject.raised_amount,
        targetFunding: apiProject.goal_amount,
        category: apiProject.category || "",
        status: apiProject.status as "active" | "completed" | "planned",
        coordinators: apiProject.coordinators || [],
        startDate: apiProject.start_date || "",
        endDate: apiProject.end_date || "",
        location: apiProject.location || "",
      }
    : null;

  return {
    data: mappedProject,
    isLoading,
    error,
  };
};
